import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "store/Store";
import mockupService from "services/mockupService";
import { extractErrorMessage } from "utils/ErrorHandlers";
import { handleMockUpNavigation } from "utils/authHandlers";
import { ICartItem } from "store/types/cartTypes";

interface ConfiguredCombination {
  productId: string;
  configuredCombinations: string[];
}

interface ConfigurationState {
  pdpConfigurations: { [productId: string]: ConfiguredCombination };

  cartConfigurations: { [productId: string]: string[] };
  cartMockupAvailability: { [itemKey: string]: boolean };
  cartLoading: boolean;
  cartFetchedNPINs: string[];
  isCartConfigurationsLoaded: boolean;

  pdpLoading: boolean;
  error: string | null;
}

const initialState: ConfigurationState = {
  pdpConfigurations: {},
  cartConfigurations: {},
  cartMockupAvailability: {},
  pdpLoading: false,
  cartLoading: false,
  error: null,
  cartFetchedNPINs: [],
  isCartConfigurationsLoaded: false,
};

const extractConfigurationId = (url: string): string => {
  const parts = url.split("/");
  return parts[parts.length - 1];
};

const calculateMockupAvailability = (
  items: ICartItem[],
  configuredCombinations: { [productId: string]: string[] }
): { [itemKey: string]: boolean } => {
  const availability: { [itemKey: string]: boolean } = {};

  items.forEach((item) => {
    const itemKey = `${item.id}-${item.variantId}`;
    const npin = item.identification?.NPIN;

    if (!npin) {
      availability[itemKey] = false;
      return;
    }

    const variantAttributes = item.variants?.[0]?.attributes || {};

    const finalUrl = handleMockUpNavigation(item, variantAttributes);

    if (finalUrl && configuredCombinations[npin]) {
      const configurationId = extractConfigurationId(finalUrl);

      const combinations = configuredCombinations[npin];

      const isAvailable = combinations.some(
        (combo) => combo.toLowerCase() === configurationId.toLowerCase()
      );

      availability[itemKey] = isAvailable;
    } else {
      availability[itemKey] = false;
    }
  });

  return availability;
};

export const fetchPDPConfigurationAsync = createAsyncThunk(
  "configuration/fetchPDPConfiguration",
  async (productId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;

      const existingConfiguration =
        selectPDPConfigurationByProductId(productId)(state);

      if (existingConfiguration) {
        return {
          productId,
          configuration: existingConfiguration,
          fetchedNewData: false,
        };
      }

      const response = await mockupService.getConfiguredCombinations([
        productId,
      ]);
      const responseData = response?.data || response;

      const configuration = responseData?.[0];

      if (!configuration) {
        throw new Error(`No configuration found for product ${productId}`);
      }

      return {
        productId,
        configuration,
        fetchedNewData: true,
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const fetchCartConfigurationsAsync = createAsyncThunk(
  "configuration/fetchCartConfigurations",
  async (
    { npins, cartItems }: { npins: string[]; cartItems: ICartItem[] },
    { getState, rejectWithValue }
  ) => {
    try {
      if (npins.length === 0) {
        return {
          data: [],
          fetchedNPINs: [],
          availability: {},
          configurations: {},
        };
      }

      const state = getState() as RootState;

      const existingConfigurations = selectCartConfigurations(state);
      const lastFetchedNPINs = selectCartFetchedNPINs(state);

      const newNPINs = npins.filter((npin) => !lastFetchedNPINs.includes(npin));

      let combinationsMap = { ...existingConfigurations };

      let responseData: ConfiguredCombination[] = [];

      if (newNPINs.length > 0) {
        const response = await mockupService.getConfiguredCombinations(
          newNPINs
        );
        responseData = response?.data || response;

        responseData?.forEach((item: ConfiguredCombination) => {
          combinationsMap[item.productId] = item.configuredCombinations || [];
        });
      }

      const availability = calculateMockupAvailability(
        cartItems,
        combinationsMap
      );

      return {
        data: responseData || [],
        fetchedNPINs: newNPINs,
        allNPINs: npins,
        availability,
        configurations: combinationsMap,
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const fetchConfigurationForNewCartItemAsync = createAsyncThunk(
  "configuration/fetchConfigurationForNewCartItem",
  async (
    {
      newNPIN,
      allCartItems,
    }: {
      newNPIN: string;
      allCartItems: ICartItem[];
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;

      // Use selectors instead of direct state access
      const existingConfigurations = selectCartConfigurations(state);
      const lastFetchedNPINs = selectCartFetchedNPINs(state);

      if (lastFetchedNPINs.includes(newNPIN)) {
        const availability = calculateMockupAvailability(
          allCartItems,
          existingConfigurations
        );
        return {
          configurations: existingConfigurations,
          availability,
          fetchedNewData: false,
        };
      }

      const response = await mockupService.getConfiguredCombinations([newNPIN]);
      const responseData = response?.data || response;

      const combinationsMap = { ...existingConfigurations };
      responseData?.forEach((item: ConfiguredCombination) => {
        combinationsMap[item.productId] = item.configuredCombinations || [];
      });

      const availability = calculateMockupAvailability(
        allCartItems,
        combinationsMap
      );

      return {
        configurations: combinationsMap,
        availability,
        fetchedNewData: true,
        fetchedNPINs: [newNPIN],
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const updateCartMockupAvailabilityAsync = createAsyncThunk(
  "configuration/updateCartMockupAvailability",
  async (cartItems: ICartItem[], { getState }) => {
    const state = getState() as RootState;

    // Use selector instead of direct state access
    const cartConfigurations = selectCartConfigurations(state);

    return calculateMockupAvailability(cartItems, cartConfigurations);
  }
);

const configurationSlice = createSlice({
  name: "configuration",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    clearPDPConfigurations: (state) => {
      state.pdpConfigurations = {};
    },

    clearCartConfigurations: (state) => {
      state.cartConfigurations = {};
      state.cartMockupAvailability = {};
      state.cartFetchedNPINs = [];
      state.isCartConfigurationsLoaded = false;
    },

    clearAllConfigurations: (state) => {
      state.pdpConfigurations = {};
      state.cartConfigurations = {};
      state.cartMockupAvailability = {};
      state.cartFetchedNPINs = [];
      state.isCartConfigurationsLoaded = false;
    },

    removeItemsFromCartAvailability: (
      state,
      action: PayloadAction<string[]>
    ) => {
      const itemKeysToRemove = action.payload;
      itemKeysToRemove.forEach((itemKey) => {
        delete state.cartMockupAvailability[itemKey];
      });
    },

    resetConfigurations: (state) => {
      state.pdpConfigurations = {};
      state.cartConfigurations = {};
      state.cartMockupAvailability = {};
      state.cartFetchedNPINs = [];
      state.error = null;
      state.isCartConfigurationsLoaded = false;
    },

    setCartMockupAvailability: (
      state,
      action: PayloadAction<{ [itemKey: string]: boolean }>
    ) => {
      state.cartMockupAvailability = {
        ...state.cartMockupAvailability,
        ...action.payload,
      };
    },

    setCartConfigurationsLoaded: (state, action: PayloadAction<boolean>) => {
      state.isCartConfigurationsLoaded = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchPDPConfigurationAsync.pending, (state) => {
        state.pdpLoading = true;
        state.error = null;
      })
      .addCase(fetchPDPConfigurationAsync.fulfilled, (state, action) => {
        state.pdpLoading = false;
        state.error = null;

        const { productId, configuration, fetchedNewData } = action.payload;

        // Only store if we fetched new data
        if (fetchedNewData) {
          state.pdpConfigurations[productId] = configuration;
        }
      })
      .addCase(fetchPDPConfigurationAsync.rejected, (state, action) => {
        state.pdpLoading = false;
        state.error = action.payload as string;
      })

      // Fetch cart configurations (bulk)
      .addCase(fetchCartConfigurationsAsync.pending, (state) => {
        state.cartLoading = true;
        state.error = null;
      })
      .addCase(fetchCartConfigurationsAsync.fulfilled, (state, action) => {
        state.cartLoading = false;
        state.error = null;

        const { configurations, availability, fetchedNPINs } = action.payload;

        // Update cart configurations
        state.cartConfigurations = {
          ...state.cartConfigurations,
          ...configurations,
        };

        // Update cart fetched NPINs with newly fetched ones
        state.cartFetchedNPINs = Array.from(
          new Set([...state.cartFetchedNPINs, ...fetchedNPINs])
        );

        // Update cart mockup availability
        state.cartMockupAvailability = {
          ...state.cartMockupAvailability,
          ...availability,
        };

        state.isCartConfigurationsLoaded = true;
      })
      .addCase(fetchCartConfigurationsAsync.rejected, (state, action) => {
        state.cartLoading = false;
        state.error = action.payload as string;
      })

      // Fetch configuration for new cart item (single NPIN for cart)
      .addCase(fetchConfigurationForNewCartItemAsync.pending, (state) => {
        state.cartLoading = true;
        state.error = null;
      })
      .addCase(
        fetchConfigurationForNewCartItemAsync.fulfilled,
        (state, action) => {
          state.cartLoading = false;
          state.error = null;

          const { configurations, availability, fetchedNewData, fetchedNPINs } =
            action.payload;

          if (fetchedNewData && fetchedNPINs) {
            state.cartConfigurations = {
              ...state.cartConfigurations,
              ...configurations,
            };
            state.cartFetchedNPINs = Array.from(
              new Set([...state.cartFetchedNPINs, ...fetchedNPINs])
            );
          }

          // Always update cart availability
          state.cartMockupAvailability = {
            ...state.cartMockupAvailability,
            ...availability,
          };

          state.isCartConfigurationsLoaded = true;
        }
      )
      .addCase(
        fetchConfigurationForNewCartItemAsync.rejected,
        (state, action) => {
          state.cartLoading = false;
          state.error = action.payload as string;
        }
      )

      .addCase(updateCartMockupAvailabilityAsync.fulfilled, (state, action) => {
        state.cartMockupAvailability = {
          ...state.cartMockupAvailability,
          ...action.payload,
        };
      });
  },
});

export const {
  clearError,
  clearPDPConfigurations,
  clearCartConfigurations,
  clearAllConfigurations,
  removeItemsFromCartAvailability,
  resetConfigurations,
  setCartMockupAvailability,
  setCartConfigurationsLoaded,
} = configurationSlice.actions;

export default configurationSlice.reducer;

// Selectors
export const selectPDPConfigurations = (state: RootState) =>
  state.configuration.pdpConfigurations;

export const selectPDPConfigurationLoading = (state: RootState) =>
  state.configuration.pdpLoading;

export const selectPDPConfigurationByProductId =
  (productId: string) => (state: RootState) =>
    state.configuration.pdpConfigurations[productId] || null;

export const selectPDPConfigurationExists =
  (productId: string) => (state: RootState) =>
    !!state.configuration.pdpConfigurations[productId];

export const selectCartConfigurations = (state: RootState) =>
  state.configuration.cartConfigurations;

export const selectCartMockupAvailability = (state: RootState) =>
  state.configuration.cartMockupAvailability;

export const selectCartConfigurationLoading = (state: RootState) =>
  state.configuration.cartLoading;

export const selectIsCartConfigurationsLoaded = (state: RootState) =>
  state.configuration.isCartConfigurationsLoaded;

export const selectCartFetchedNPINs = (state: RootState) =>
  state.configuration.cartFetchedNPINs;

export const selectConfigurationError = (state: RootState) =>
  state.configuration.error;

export const selectCartMockupAvailabilityForItem =
  (itemKey: string) => (state: RootState) =>
    state.configuration.cartMockupAvailability[itemKey] || false;

export const selectCartNPINsNeedingFetch =
  (npins: string[]) => (state: RootState) => {
    const fetchedNPINs = state.configuration.cartFetchedNPINs;
    return npins.filter((npin) => !fetchedNPINs.includes(npin));
  };
